import React, { useState } from "react";
import { Popover, Typography } from "@mui/material";
import stickyImage from "../../../../styles/svg/sticky-icon.svg";
import { SanitizedHtml } from "../../../../utils";

/**
 * Reusable Note Popover component for displaying note previews on hover
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the popover is open
 * @param {HTMLElement} props.anchorEl - The element to anchor the popover to
 * @param {Function} props.onClose - Function to call when popover is closed
 * @param {string} props.content - The note content to display
 * @param {string} props.date - The date to display
 * @param {string} props.backgroundColor - The background color for the popover (default: "#fff9c4")
 * @param {Object} props.anchorOrigin - The anchor origin for the popover
 * @param {Object} props.transformOrigin - The transform origin for the popover
 */
const NotePopover = ({
  open,
  anchorEl,
  onClose,
  content,
  title,
  date,
  backgroundColor = "#fff9c4",
  anchorOrigin = {
    vertical: "bottom",
    horizontal: "right",
  },
  transformOrigin = {
    vertical: "top",
    horizontal: "left",
  }
}) => {
  const [isPopoverHovered, setIsPopoverHovered] = useState(false);

  // Handler to track when mouse enters the popover content
  const handlePopoverMouseEnter = () => {
    setIsPopoverHovered(true);
  };

  // Handler to track when mouse leaves the popover content
  const handlePopoverMouseLeave = () => {
    setIsPopoverHovered(false);
    // Only close if we've left the popover (anchorEl's onMouseLeave will handle its own case)
    onClose();
  };

  return (
    <Popover
      open={open || isPopoverHovered}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      disableRestoreFocus
      PaperProps={{
        style: {
          maxWidth: "250px",
          maxHeight: "250px",
          overflow: "auto",
          padding: "16px",
          backgroundColor: backgroundColor,
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
          position: "relative",
          backgroundSize: "24px",
          backgroundPosition: "bottom right",
          backgroundRepeat: "no-repeat",
        },
        onMouseEnter: handlePopoverMouseEnter,
        onMouseLeave: handlePopoverMouseLeave,
      }}
      sx={{
        pointerEvents: "none",
        zIndex: 1500,
      }}
    >
      <div className="note-preview-content">
        <Typography sx={{fontSize: "15px", fontWeight: "bold"}}>{title}</Typography>
        <SanitizedHtml
          as="div"
          style={{
            whiteSpace: "pre-wrap",
            fontSize: "14px",
            lineHeight: "1.5",
            maxHeight: "180px",
            overflowY: "auto",
            color: "#212B36",
            fontFamily: "inherit",
          }}
          html={content.replace(/\n/g, "<br/>")}
        />
        {date && (
          <div
            style={{
              marginTop: "12px",
              fontSize: "12px",
              opacity: 0.7,
              fontStyle: "italic",
              position: "relative",
              color: "#212B36",
            }}
          >
            {date}
          </div>
        )}
      </div>
    </Popover>
  );
};

export default NotePopover; 
